// New-order alert sound. Uses a real bundled audio file played via an
// HTMLAudioElement — far more reliable & louder than synthesized WebAudio,
// especially on phones and in background tabs.

const BASE = process.env.PUBLIC_URL || "";

// Selectable alert sounds (bundled WAV files in /public).
export const ALERT_SOUNDS = [
  { key: "chime", label: "Sweet Chime", file: "/alert.wav" },
  { key: "doorbell", label: "Doorbell", file: "/alert-doorbell.wav" },
  { key: "marimba", label: "Marimba", file: "/alert-marimba.wav" },
  { key: "alarm", label: "Digital Alarm", file: "/alert-alarm.wav" },
];
const DEFAULT_SOUND = "chime";

export function getAlertSound() {
  const k = localStorage.getItem("vendor_alert_sound");
  return ALERT_SOUNDS.some((s) => s.key === k) ? k : DEFAULT_SOUND;
}

export function setAlertSound(key) {
  const found = ALERT_SOUNDS.find((s) => s.key === key);
  if (!found) return;
  localStorage.setItem("vendor_alert_sound", key);
  if (audioEl) audioEl.src = `${BASE}${found.file}`; // apply immediately
}

function currentFile() {
  const s = ALERT_SOUNDS.find((x) => x.key === getAlertSound()) || ALERT_SOUNDS[0];
  return `${BASE}${s.file}`;
}

let audioEl = null;
let unlocked = false;

function getAudio() {
  if (!audioEl) {
    audioEl = new Audio(currentFile());
    audioEl.preload = "auto";
    audioEl.loop = true; // keeps ringing until we pause it
    audioEl.volume = 1.0;
  }
  return audioEl;
}

// Must be called from a user gesture (e.g. tapping "Alerts on") so the browser
// allows the sound to be replayed later without a gesture.
export function initAudio() {
  const a = getAudio();
  unlocked = true;
  return a;
}

// Play the chime once (used as a preview when the vendor enables alerts).
export function playChime() {
  try {
    const a = getAudio();
    unlocked = true;
    a.loop = false;
    a.currentTime = 0;
    const p = a.play();
    if (p && p.catch) p.catch((e) => console.warn("Chime play blocked:", e));
    // restore loop for any subsequent ringing
    a.onended = () => { a.loop = true; };
  } catch (e) {
    console.warn("Chime playback failed:", e);
  }
}

// Play a specific sound once (used to preview options in the sound picker).
let previewEl = null;
export function previewSound(key) {
  try {
    const s = ALERT_SOUNDS.find((x) => x.key === key);
    if (!s) return;
    unlocked = true;
    if (!previewEl) previewEl = new Audio();
    previewEl.src = `${BASE}${s.file}`;
    previewEl.loop = false;
    previewEl.currentTime = 0;
    const p = previewEl.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) {
    console.warn("Preview failed:", e);
  }
}

// Repeating alert: loops the chime until stopped (vendor accepts/rejects clears
// new orders) or the max duration elapses. A Web Worker timer enforces the cap
// and re-kicks the audio if the OS paused it (background reliability).
let ringActive = false;
let ringStartedAt = 0;
let ringMaxMs = 5 * 60 * 1000;
let ringWorker = null;
let ringFallback = null;

function kick() {
  if (!ringActive) return;
  if (Date.now() - ringStartedAt >= ringMaxMs) {
    stopRinging();
    return;
  }
  const a = getAudio();
  if (a.paused) {
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  }
}

function ensureWorker() {
  if (ringWorker || ringFallback) return;
  try {
    const src =
      "let id=null;self.onmessage=function(e){var d=e.data||{};" +
      "if(d.cmd==='start'){if(id)clearInterval(id);id=setInterval(function(){self.postMessage('tick');},d.interval||3000);}" +
      "else if(d.cmd==='stop'){if(id){clearInterval(id);id=null;}}};";
    const blob = new Blob([src], { type: "application/javascript" });
    ringWorker = new Worker(URL.createObjectURL(blob));
    ringWorker.onmessage = kick;
  } catch {
    ringWorker = null;
  }
}

export function startRinging({ maxMs = 5 * 60 * 1000 } = {}) {
  if (ringActive) return;
  ringActive = true;
  ringStartedAt = Date.now();
  ringMaxMs = maxMs;
  const a = getAudio();
  a.loop = true;
  a.currentTime = 0;
  const p = a.play();
  if (p && p.catch) p.catch((e) => console.warn("Ring play blocked (needs a tap on 'Alerts on' first):", e));
  ensureWorker();
  if (ringWorker) ringWorker.postMessage({ cmd: "start", interval: 3000 });
  else ringFallback = setInterval(kick, 3000);
}

export function stopRinging() {
  ringActive = false;
  if (audioEl) {
    audioEl.pause();
    audioEl.currentTime = 0;
  }
  if (ringWorker) ringWorker.postMessage({ cmd: "stop" });
  if (ringFallback) {
    clearInterval(ringFallback);
    ringFallback = null;
  }
}

export function isRinging() {
  return ringActive;
}

export function isUnlocked() {
  return unlocked;
}

export async function requestNotify() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export function notify(title, body) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, {
        body,
        tag: "vendor-new-order",
        renotify: true,
        requireInteraction: true,
      });
    }
  } catch (e) {
    console.warn("Browser notification failed:", e);
  }
}
